import * as React from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridToolbar,
  GridCsvExportOptions,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PersonIcon from '@mui/icons-material/Person';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  deleteOne as deleteEmployee,
  getMany as getEmployees,
  type Employee,
} from '../data/employees';
import { getAllBranches } from '../data/branches';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import {common} from "@mui/material/colors";
import CustomTablePagination from './CustomTablePagination';
import { useAuth } from '../../contexts/AuthContext';

const INITIAL_PAGE_SIZE = 25;
const GM_GRID_PAGE_SIZE = 100;

function EmployeeCardSkeleton() {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 1,
        backgroundColor: 'transparent',
      }}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Skeleton variant="circular" width={80} height={80} animation="wave" />
          <Skeleton variant="text" width="60%" height={24} animation="wave" />
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Skeleton variant="circular" width={32} height={32} animation="wave" />
        <Skeleton variant="circular" width={32} height={32} animation="wave" />
      </CardActions>
    </Card>
  );
}

interface EmployeeCardProps {
  employee: Employee;
  onEdit: (event: React.MouseEvent) => void;
  onDelete: (event: React.MouseEvent) => void;
  onClick: () => void;
}

function EmployeeCard({ employee, onEdit, onDelete, onClick }: EmployeeCardProps) {
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const [imageLoading, setImageLoading] = React.useState(true);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        boxShadow: 1,
        backgroundColor: 'transparent',
      }}
      onClick={onClick}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 0.5 }}>
        <Stack spacing={2} alignItems="center">
          <Box sx={{ position: 'relative', width: 100, height: 100 }}>
            {employee.profile_photo_path ? (
              <>
                {imageLoading && (
                  <Skeleton
                    variant="circular"
                    width={100}
                    height={100}
                    animation="wave"
                    sx={{ position: 'absolute', top: 0, left: 0 }}
                  >
                    <Avatar sx={{ width: 100, height: 100 }}>
                      <PersonIcon sx={{ fontSize: 48 }} />
                    </Avatar>
                  </Skeleton>
                )}
                <Avatar
                  src={employee.profile_photo_path}
                  alt={employee.name}
                  sx={{
                    width: 100,
                    height: 100,
                    opacity: imageLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  imgProps={{
                    onLoad: () => setImageLoading(false),
                    onError: () => setImageLoading(false),
                  }}
                />
              </>
            ) : (
              <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 48 }} />
              </Avatar>
            )}
            <Tooltip title={employee.isOnline ? t('employees.online') : t('employees.offline')} arrow>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: employee.isOnline ? 'success.main' : 'error.main',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              />
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'center'}}>
        <Divider sx={{ width: '50%' }} />
      </Box>
      <CardContent sx={{ textAlign: 'center', py: 0.5 }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            width: '100%',
          }}
        >
          {employee.name}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label="edit"
          color="primary"
          sx={{
              '&:hover': {
                  backgroundColor: '#31394a',
                  color: 'white'
              },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onDelete}
          aria-label="delete"
          color="error"
          sx={{ '&:hover': {
                  background: 'red',
                  color: 'white'
              },
              marginRight:  `${isRtl? 8 : 8 }px`,
              marginLeft:  `${isRtl? 0 : 8 }px`
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}

export default function EmployeeList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const dialogs = useDialogs();
  const notifications = useNotification();

  const getInitialPageSize = () => {
    if (searchParams.get('pageSize')) {
      return Number(searchParams.get('pageSize'));
    }
    return INITIAL_PAGE_SIZE;
  };

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: getInitialPageSize(),
  });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = React.useState<{
    rows: Employee[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [viewMode, setViewMode] = React.useState<'grid' | 'table'>('grid');
  const [nameFilter, setNameFilter] = React.useState('');
  const [branchFilter, setBranchFilter] = React.useState<number | ''>('');
  const [branches, setBranches] = React.useState<Array<{ id: number; name: string }>>([]);
  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Default column visibility - specify which columns to hide by default
  const defaultColumnVisibility: GridColumnVisibilityModel = {
    email: false,           // Hidden by default
    salary: false,          // Hidden by default
    role_name: false,       // Hidden by default
  };

  // Column visibility state - load from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
    const saved = localStorage.getItem('employeeListColumnVisibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultColumnVisibility;
      }
    }
    return defaultColumnVisibility;
  });

  // Save column visibility to localStorage whenever it changes
  const handleColumnVisibilityModelChange = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem('employeeListColumnVisibility', JSON.stringify(newModel));
    },
    [],
  );

  const isGMInGridView = user?.role === 'GM' && viewMode === 'grid';

  React.useEffect(() => {
    if (user?.role === 'GM' && viewMode === 'grid') {
      setPaginationModel(prev => {
        if (prev.pageSize !== GM_GRID_PAGE_SIZE) {
          return {
            page: 0,
            pageSize: GM_GRID_PAGE_SIZE,
          };
        }
        return prev;
      });
    } else if (user?.role === 'GM' && viewMode === 'table') {
      // Only update if pageSize is not already set to INITIAL_PAGE_SIZE
      setPaginationModel(prev => {
        if (prev.pageSize !== INITIAL_PAGE_SIZE) {
          return {
            page: 0,
            pageSize: INITIAL_PAGE_SIZE,
          };
        }
        return prev;
      });
    }
  }, [viewMode, user?.role]);

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchesData = await getAllBranches();
        setBranches(branchesData);
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    };
    fetchBranches();
  }, []);

  React.useEffect(() => {
    const fetchData = async () => {
      setError(null);
      setIsLoading(true);

      try {
        const branchId = user?.role === 'BM' ? user.branch_id : undefined;

        const listData = await getEmployees({
          paginationModel,
          sortModel,
          filterModel,
          branchId,
        });

        setRowsState({
          rows: listData.items,
          rowCount: listData.itemCount,
        });
      } catch (listDataError) {
        setError(listDataError as Error);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [paginationModel, sortModel, filterModel, user]);

  const handleRefresh = React.useCallback(async () => {
    if (!isLoading) {
      setError(null);
      setIsLoading(true);

      try {
        // For BM role, filter by their branch_id
        const branchId = user?.role === 'BM' ? user.branch_id : undefined;

        const listData = await getEmployees({
          paginationModel,
          sortModel,
          filterModel,
          branchId,
        });

        setRowsState({
          rows: listData.items,
          rowCount: listData.itemCount,
        });
      } catch (listDataError) {
        setError(listDataError as Error);
      }

      setIsLoading(false);
    }
  }, [isLoading, paginationModel, sortModel, filterModel, user]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/employees/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    navigate('/employees/new');
  }, [navigate]);

  const handleRowEdit = React.useCallback(
    (employee: Employee) => () => {
      navigate(`/employees/${employee.id}/edit`);
    },
    [navigate],
  );

  const handleRowDelete = React.useCallback(
    (employee: Employee) => async () => {
      const confirmed = await dialogs.confirm(
        `${t('messages.deleteConfirm')}`,
        {
          title: t('employees.delete'),
          severity: 'error',
          okText: t('common.delete'),
          cancelText: t('common.cancel'),
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await deleteEmployee(Number(employee.id));

          notifications.show(t('messages.deleteSuccess'), {
            severity: 'success',
            autoHideDuration: 3000,
          });

          // Reload data after deletion
          try {
            // For BM role, filter by their branch_id
            const branchId = user?.role === 'BM' ? user.branch_id : undefined;

            const listData = await getEmployees({
              paginationModel,
              sortModel,
              filterModel,
              branchId,
            });

            setRowsState({
              rows: listData.items,
              rowCount: listData.itemCount,
            });
          } catch (listDataError) {
            setError(listDataError as Error);
          }
        } catch (deleteError) {
          notifications.show(
            `${t('messages.errorOccurred')}: ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, notifications, paginationModel, sortModel, filterModel, user],
  );

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );
    const { direction } = useLanguage();
    const isRtl = direction === 'rtl';
  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: t('employees.name'),
        width: 120,
        minWidth: 100,
        flex: 0.8,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'email',
        headerName: t('employees.email'),
        width: 150,
        minWidth: 120,
        flex: 1,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'branch',
        headerName: t('employees.branch'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        valueGetter: (value: { name?: string } | null | undefined) => value?.name || '',
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'role_name',
        headerName: t('employees.role'),
        type: 'singleSelect',
        valueOptions: ['GM', 'BM', 'Employee'],
        width: 100,
        minWidth: 90,
        flex: 0.6,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'salary',
        headerName: t('employees.salary'),
        type: 'number',
        width: 100,
        minWidth: 90,
        flex: 0.6,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'available_leave',
        headerName: t('employees.availableLeave'),
        type: 'number',
        width: 110,
        minWidth: 100,
        flex: 0.7,
        align: isRtl ? 'right' : 'left',
        headerAlign: 'left',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'isOnline',
        headerName: t('employees.status'),
        width: 100,
        minWidth: 90,
        flex: 0.6,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: ({ row }) => (
          <Tooltip title={row.isOnline ? t('employees.online') : t('employees.offline')} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: row.isOnline ? 'success.main' : 'error.main',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ color: row.isOnline ? 'success.main' : 'error.main', fontWeight: 500 }}>
                {row.isOnline ? t('employees.online') : t('employees.offline')}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: t('common.actions'),
        type: 'actions',
        width: 100,
        minWidth: 90,
        align: 'right',
        headerAlign: 'right',
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit-item"
            icon={<EditIcon />}
            label={t('common.edit')}
            onClick={handleRowEdit(row)}
          />,
          <GridActionsCellItem
            key="delete-item"
            icon={<DeleteIcon />}
            label={t('common.delete')}
            onClick={handleRowDelete(row)}
            style={{
                  background: 'red',
                  color: 'white'
          }}
          />,
        ],
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
    ],
    [handleRowEdit, handleRowDelete, t, isRtl],
  );

  // CSV export options with UTF-8 BOM for Arabic support
  const csvOptions: GridCsvExportOptions = React.useMemo(
    () => ({
      utf8WithBom: true,
    }),
    [],
  );

  const filteredRows = React.useMemo(() => {
    return rowsState.rows.filter((employee) => {
      const matchesName = nameFilter === '' ||
        employee.name.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesBranch = branchFilter === '' ||
        (typeof branchFilter === 'number' && employee.branch_id === branchFilter);
      return matchesName && matchesBranch;
    });
  }, [rowsState.rows, nameFilter, branchFilter]);

  const handleColumnMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = React.useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleToggleColumn = React.useCallback(
    (field: string) => {
      setColumnVisibilityModel((prev) => {
        const newModel = {
          ...prev,
          [field]: prev[field] === false ? true : false,
        };
        localStorage.setItem('employeeListColumnVisibility', JSON.stringify(newModel));
        return newModel;
      });
    },
    [],
  );

  // Define available columns for visibility toggle (excluding actions)
  const availableColumns = React.useMemo(() => [
    { field: 'name', label: t('employees.name') },
    { field: 'email', label: t('employees.email') },
    { field: 'branch', label: t('employees.branch') },
    { field: 'role_name', label: t('employees.role') },
    { field: 'salary', label: t('employees.salary') },
    { field: 'available_leave', label: t('employees.availableLeave') },
    { field: 'isOnline', label: t('employees.status') },
  ], [t]);

  const pageTitle = t('employees.title');
  return (
    <PageContainer
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={viewMode !== 'grid' ? t('employees.gridView') : t('employees.tableView')} placement="bottom" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="toggle view"
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
                sx={{ marginLeft:  isRtl? 1 : 0 , }}
              >
                {viewMode === 'grid' ? <TableRowsIcon /> : <GridViewIcon />}
              </IconButton>
            </div>
          </Tooltip>
          {viewMode === 'table' && (
              <Tooltip title="Toggle Columns" placement="bottom" enterDelay={1000}>
                <IconButton
                    size="small"
                    aria-label="toggle columns"
                    onClick={handleColumnMenuOpen}
                >
                  <ViewColumnIcon />
                </IconButton>
              </Tooltip>
          )}
          <Tooltip title={t('common.refresh')} placement="bottom" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <RtlButton
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
          >
            {t('common.create')}
          </RtlButton>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: '100%'
        }}
      >
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
              <TextField
                size="small"
                label={t('employees.name')}
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{ minWidth: 200}}
                InputLabelProps={{
                  sx: {
                    right: isRtl ? 1 : 'auto',
                    left: isRtl ? 'auto' : 0
                  }
                }}
              />
                { user.role === 'GM' && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{right: isRtl ? 1 : 'auto', left: isRtl ? 'auto' : 0 }}>
                            {t('employees.branch')}
                        </InputLabel>
                        <Select
                            value={branchFilter === '' ? '' : String(branchFilter)}
                            label={t('employees.branch')}
                            onChange={(e: SelectChangeEvent) => {
                                const value = e.target.value;
                                setBranchFilter(value === '' ? '' : Number(value));
                            }}
                        >
                            <MenuItem value="">{t('common.all')}</MenuItem>
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={String(branch.id)}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

            </Box>
            {viewMode === 'grid' ? (
              <Box sx={{ width: '100%' }}>
                <Grid container spacing={3}>
                  {isLoading
                    ? Array.from(new Array(isGMInGridView ? GM_GRID_PAGE_SIZE : paginationModel.pageSize)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`skeleton-${index}`}>
                          <EmployeeCardSkeleton />
                        </Grid>
                      ))
                    : filteredRows.map((employee) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={employee.id}>
                          <EmployeeCard
                            employee={employee}
                            onEdit={(event) => {
                              event.stopPropagation();
                              handleRowEdit(employee)();
                            }}
                            onDelete={(event) => {
                              event.stopPropagation();
                              handleRowDelete(employee)();
                            }}
                            onClick={() => navigate(`/employees/${employee.id}`)}
                          />
                        </Grid>
                      ))}
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    flex: 1
                  }}
                >
                  <DataGrid
                    rows={filteredRows}
                    rowCount={filteredRows.length}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    pagination
                    paginationMode="client"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    disableRowSelectionOnClick
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                    onRowClick={handleRowClick}
                    loading={isLoading}
                    initialState={initialState}
                    pageSizeOptions={[INITIAL_PAGE_SIZE, 50, 75, 100]}
                    hideFooter
                    localeText={{
                      noRowsLabel: t('dataGrid.noRowsLabel'),
                      noResultsOverlayLabel: t('dataGrid.noResultsOverlayLabel'),
                    }}
                    slotProps={{
                      loadingOverlay: {
                        variant: 'circular-progress',
                        noRowsVariant: 'circular-progress',
                      },
                      baseIconButton: {
                        size: 'small',
                      },
                    }}
                    sx={{
                      height: '100%',
                      width: '100%',
                      [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                        outline: 'transparent',
                      },
                      [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                        {
                          outline: 'none',
                        },
                      [`& .${gridClasses.row}:hover`]: {
                        cursor: 'pointer',
                      },
                      [`& .${gridClasses.columnSeparator}`]: {
                        display: 'none',
                      },
                      [`& .${gridClasses.columnHeader}`]: {
                        cursor: 'default !important',
                      },
                    }}
                  />
                </Box>
                <CustomTablePagination
                  paginationModel={paginationModel}
                  rowCount={filteredRows.length}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={[INITIAL_PAGE_SIZE, 50, 75, 100]}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={0.5}>
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={columnVisibilityModel[col.field] !== false}
                    onChange={() => handleToggleColumn(col.field)}
                    size="small"
                  />
                }
                label={col.label}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </PageContainer>
  );
}
